import {useEffect, useState} from 'react';
import {Form, Input} from 'antd';
import {useMutation} from '@apollo/client';
import {UPDATE_APP_CONFIGURATION} from 'graphql/queries/mobileSettings';
import {TRANSLATE_TEXT} from 'graphql/mutations/notifications';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {toast} from 'react-toastify';
import {Button} from '@dabafinance/react-components';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import styles from '../withdrawalReason/withdrawalReasons.module.scss';
import deleteIcon from 'images/delete.svg';
import plusIcon from 'images/plus-circle.png';

const {TextArea} = Input;

const FIELD_LIST = 'whatWouldYouUseDabaFor';

const getDefaultOption = () => ({
  EN: '',
  FR: '',
});

export const WhatWouldYouUseDabaFor = ({data, refetch}) => {
  const [form] = Form.useForm();
  const [openFinishModal, setOpenFinishModal] = useState(false);

  const [translateText, {loading: loadingTranslation}] =
    useMutation(TRANSLATE_TEXT);

  const [updateAppConfig, {loading: loadingUpdate}] = useMutation(
    UPDATE_APP_CONFIGURATION,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  useEffect(() => {
    if (data && Array.isArray(data)) {
      const formattedData = data.map(item => ({
        EN: item.EN || '',
        FR: item.FR || '',
      }));
      form.setFieldsValue({[FIELD_LIST]: formattedData});
    } else {
      form.setFieldsValue({[FIELD_LIST]: []});
    }
  }, [data, form]);

  const handleTranslate = async fieldName => {
    const englishText = form.getFieldValue([FIELD_LIST, fieldName, 'EN']);

    if (!englishText?.trim()) {
      toast.error('Please enter text in English first');
      return;
    }

    try {
      const {data: translateData} = await translateText({
        variables: {
          sourceLang: 'EN',
          targetLang: 'FR',
          sourceText: englishText,
        },
      });

      if (
        translateData?.translateText?.statusCode === 400 ||
        translateData?.translateText?.__typename === 'Error'
      ) {
        toast.error(
          'Error Translating Text',
          translateData?.translateText?.message,
        );
      } else {
        const translatedText = translateData?.translateText?.targetText || '';
        form.setFieldsValue({
          [FIELD_LIST]: form.getFieldValue(FIELD_LIST).map((item, index) => {
            if (index === fieldName) {
              return {
                ...item,
                FR: translatedText,
              };
            }
            return item;
          }),
        });
        toast.success('Translated Successfully');
      }
    } catch (error) {
      toast.error('Translation failed');
    }
  };

  const handleSubmit = () => {
    const formValues = form.getFieldsValue();
    const formattedData = (formValues[FIELD_LIST] || []).map(item => ({
      EN: item.EN || '',
      FR: item.FR || '',
    }));

    updateAppConfig({
      variables: {
        data: {
          [FIELD_LIST]: formattedData,
        },
      },
    })
      .then(({data: {updateAppConfig: result}}) => {
        if (result.__typename === 'Error') {
          toast.error(result.message);
        } else {
          toast.success('Updated Successfully');
          setOpenFinishModal(false);
          if (refetch) {
            refetch();
          }
        }
      })
      .catch(error => toast.error(error.message || 'Update failed'));
  };

  return (
    <div className={styles.body}>
      <div className={styles.header}>
        <h2>What would you use Daba for?</h2>
        <p>
          Configure onboarding options shown when users choose how they plan to
          use Daba. Each option must be provided in both English and French.
        </p>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={() => setOpenFinishModal(true)}>
        <Form.List name={FIELD_LIST}>
          {(fields, {add, remove}) => (
            <>
              {fields.length === 0 ? (
                <div className={styles.emptyState}>
                  <p>
                    No options found. Click &quot;Add option&quot; to create
                    one.
                  </p>
                </div>
              ) : (
                <div className={styles.reasonsList}>
                  {fields.map(({key, name: fieldName}) => (
                    <div key={key} className={styles.reasonCard}>
                      <div className={styles.reasonHeader}>
                        <h3>Option {fieldName + 1}</h3>
                        <button
                          type="button"
                          className={styles.deleteButton}
                          onClick={() => remove(fieldName)}
                          aria-label="Delete option">
                          <img src={deleteIcon} alt="Delete" />
                        </button>
                      </div>

                      <div className={styles.reasonFields}>
                        <div className={styles.languageSection}>
                          <div className={styles.languageHeader}>
                            <h4>English</h4>
                            <Button
                              type="secondary"
                              label="Translate"
                              disabled={loadingTranslation}
                              onClick={() => handleTranslate(fieldName)}
                            />
                          </div>
                          <Form.Item
                            label="Option (English)"
                            name={[fieldName, 'EN']}
                            rules={[
                              {
                                required: true,
                                message: 'English text is required',
                              },
                            ]}>
                            <TextArea
                              rows={3}
                              placeholder="Enter option text in English"
                            />
                          </Form.Item>
                        </div>

                        <div className={styles.languageSection}>
                          <div className={styles.languageHeader}>
                            <h4>French</h4>
                          </div>
                          <Form.Item
                            label="Option (French)"
                            name={[fieldName, 'FR']}
                            rules={[
                              {
                                required: true,
                                message: 'French text is required',
                              },
                            ]}>
                            <TextArea
                              rows={3}
                              placeholder="Enter option text in French"
                            />
                          </Form.Item>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div
                className={styles.addButton}
                onClick={() => add(getDefaultOption())}>
                <img src={plusIcon} alt="Add" />
                <h1>Add option</h1>
              </div>
            </>
          )}
        </Form.List>

        <div className={styles.submitContainer}>
          <SubmitButton label="Update" />
        </div>
      </Form>

      <ConfirmModal
        openModal={openFinishModal}
        setOpenModal={() => setOpenFinishModal(false)}
        confirmText="Are you sure you want to update these options?"
        title="Confirm Update"
        loading={loadingUpdate}
        handleOperation={handleSubmit}
      />
    </div>
  );
};
